export const DocElementType = Object.freeze({
  Media: 'M',
  File: 'F',
  TextField: 'T',
  AddressField: 'A',
  TextArea: 'TA',
  SelectBox: 'S',
  SSelectBox: 'STATELOOKUP',
  RadioButton: 'R',
  SocialNumber: 'SS',
  DateField: 'D',
  Upload: 'U',
  Checkbox: 'C',
  Label: 'L',
  MediaLink: 'Z',
  DocumentLink: 'LK',
});

// User-facing names + hover help for the element type dropdown in Form Setup.
// The keys of DocElementType are developer names ('SSelectBox', 'MediaLink'); admins see these.
export const ELEMENT_TYPE_META = Object.freeze({
  [DocElementType.TextField]: {
    label: 'Text Field',
    description: 'Single line of free text.',
  },
  [DocElementType.TextArea]: {
    label: 'Text Area Field',
    description: 'Multi-line free text for longer answers.',
  },
  [DocElementType.DateField]: {
    label: 'Date Field',
    description: 'Date entry, MM/DD/YYYY.',
  },
  [DocElementType.SocialNumber]: {
    label: 'SSN / Tax ID Field',
    description:
      'Formatted as ___-__-____ as the participant types — not masked. Still treated as sensitive information.',
  },
  [DocElementType.AddressField]: {
    label: 'Address Field',
    description: 'Address lookup that autocompletes as the participant types.',
  },
  [DocElementType.RadioButton]: {
    label: 'Radio Button',
    description:
      'All options visible, participant picks one. Can reveal follow-up questions per option.',
  },
  [DocElementType.SelectBox]: {
    label: 'Dropdown Field',
    description: 'Pick one option from a dropdown. Best when there are many options.',
  },
  [DocElementType.SSelectBox]: {
    label: 'State Dropdown',
    description: 'Dropdown pre-filled with all US states.',
  },
  [DocElementType.Checkbox]: {
    label: 'Checkbox Field',
    description: 'Single tick box, for confirmations and agreements.',
  },
  [DocElementType.Upload]: {
    label: 'Upload Field',
    description: 'Participant uploads one or more files.',
  },
  // TODO: `File` (F) and `Upload` (U) are near-duplicate components — they differ only in that
  // File sends the field label as a `description` with the upload. Confirm with the team whether
  // one is legacy and should be dropped from this dropdown entirely.
  [DocElementType.File]: {
    label: 'Upload Field (with Description)',
    description: 'Same as Upload Field, and also sends the field label as a description.',
  },
  [DocElementType.Label]: {
    label: 'Static Text',
    description: 'Read-only text or heading. Collects no answer from the participant.',
  },
  [DocElementType.Media]: {
    label: 'Iframe Video',
    description:
      'Embeds a video/media URL inline. Add the URL from Field configuration (the gear icon) on this row.',
  },
  [DocElementType.MediaLink]: {
    label: 'Template Document',
    description:
      'A document you upload to this template, from Field configuration (the gear icon) on this row. Participants view or download it.',
  },
  [DocElementType.DocumentLink]: {
    label: 'Order Document',
    description: 'Link to a document attached to the specific order.',
  },
});

// Dropdown order — grouped by purpose rather than the declaration order of DocElementType.
export const ELEMENT_TYPE_ORDER = Object.freeze([
  DocElementType.TextField,
  DocElementType.TextArea,
  DocElementType.DateField,
  DocElementType.SocialNumber,
  DocElementType.AddressField,
  DocElementType.RadioButton,
  DocElementType.SelectBox,
  DocElementType.Checkbox,
  DocElementType.Upload,
  DocElementType.File,
  DocElementType.Label,
  DocElementType.Media,
  DocElementType.MediaLink,
  DocElementType.DocumentLink,
]);

export const CLIENT_ADMIN_ROLE = 'client admin';
export const CLIENT_USER_ROLE = 'client user';
export const SUPER_ADMIN_ROLE = 'super admin';
export const OFFICE_ADMIN_ROLE = 'office admin';
export const OFFICE_USER_ROLE = 'office user';
export const REALTOR_ROLE = 'realtor';
export const ADMIN_ROLE = 'admin';

export const USER_ROLE_IN_COMPANY_OPTIONS = ['Closer', 'Processor'];
export const DEFAULT_USER_ROLE_IN_COMPANY = 'Closer';

// Routes that handle breadcrumbs dynamically (don't use default breadcrumb logic)
export const DYNAMIC_BREADCRUMB_ROUTES = [
  '/admin/users',
  '/admin/integration-logs',
  '/admin/documentation',
];

/**
 * Routes that show the global Help FAB and their doc target.
 * path: prefix to match (pathname.startsWith); use trailing slash for sub-routes only.
 * fromLabel: breadcrumb label when returning from docs.
 * docSectionId: hash section on /admin/documentation.
 */
export const HELP_DOC_ROUTES = [
  {
    path: '/admin/configurations',
    exact: true,
    fromLabel: 'Configurations',
    docSectionId: 'email-reminder-configuration',
  },
  {
    path: '/admin/orders/',
    exact: false,
    fromLabel: 'Verification Screen',
    docSectionId: 'verification-screen',
  },
  {
    path: '/orders/order/',
    exact: false,
    fromLabel: 'Verification Screen',
    docSectionId: 'verification-screen',
  },
];

export const getHelpDocConfigForPath = (pathname) => {
  if (!pathname) return null;
  for (const route of HELP_DOC_ROUTES) {
    if (route.exact) {
      if (pathname === route.path) return { ...route, fromPath: pathname };
    } else {
      if (pathname.startsWith(route.path) && pathname.length > route.path.length)
        return { ...route, fromPath: pathname };
    }
  }
  return null;
};

export const LIEN_SECTION_ID = '18c8f8bb-6c90-4000-852c-d0c59a105000';
export const UTILITY_SECTION_ID = '15058e5d-e475-4f17-8e58-c076dffb0263';
export const UTILITY_RADIO_ELEMENT_ID = 'bfa68573-fe48-4b0a-898a-4dbb83b5b92f';
export const AGENT_EMAIL_FIELD_ID = '76fce2a0-2303-4f0f-8297-d824420575f9';

export const WATER_LIEN_PARENT_ID = '18c8f902-78f0-4000-815d-04f3ce24bd00';
export const SOLAR_LIEN_PARENT_ID = '18c8f8c8-d020-4000-80a9-209b9cbd3700';

export const TITLE_VESTED_NAME = 'borrower_field_98403';

export const CORE_LENDER_PUBLIC_AGREE = 'core_lender_public_agree';

export const CURRENT_ADDRESS_ELEM_NAME = 'core_current_address';
export const POST_CURRENT_ADDRESS_ELEM_NAME = 'core_post_current_address';

export const secondParticipantFields = [
  'core_buyer_fullname2',
  'buyer_v2_field_35303',
  'buyer_v2_field_31427',
  'buyer_v2_field_13711',
  'buyer_v2_field_35937',
  'buyer_v2_field_40439',
  'buyer_v2_field_25344',
  'buyer_v2_field_42164',
  'buyer_v2_field_15194',
  'buyer_v2_field_30317',
  'core_borrower_fullname2',
  'core_borrower_phone2',
  'borrower_v2_field_97372',
  'borrower_v2_field_72972',
  'borrower_v2_field_11122',
  'borrower_v2_field_45173',
  'borrower_v2_field_79431',
  'borrower_v2_field_23381',
];

export const additionalSellerFields = [
  'seller_field_23088',
  'seller_field_40229',
  'seller_field_40645',
  'seller_field_30579',
  'seller_field_65877',
];

export const spouseFields = [
  'core_spouse_first_name',
  'core_spouse_last_name',
  'core_spouse_mobile',
  'core_spouse_email',
  'core_spouse_ssn',
];

export const HOA_SECTION_ID = '57e52e39-c25d-461e-8128-2bd66ef03125';
export const CREDIT_CARD_PARENT_ID = '18ca9a49-0390-4000-8b12-903f82748c00';

export const STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
];

export const ENTITY_TYPES = ['Corporation', 'LLC', 'Trust', 'Partnership', 'Other'];
export const SOI_SECTION_ID = 'c22663ed-489b-454a-9d63-5b77579b5134';
export const MARITAL_STATUS_GROUP = 'fbebecf1-6bc0-46ba-a8ad-27a178b1a37c';
export const ADDITIONAL_SELLER_PARENT_ID = '84edef03-c02d-491d-96e1-0996fcb0db65';
export const ADDITIONAL_SELLER_GROUP_ID = '711ccb04-0bd8-4f16-aace-cfb43086897f';
export const ADDITIONAL_SELLER_OPTION_ID = 'cadc2623-8e28-4000-a217-6754798c2369';
export const SPOUSE_ADDED_FIELD_ID = '7a31928c-2363-4539-b546-49181c13898c';

export const HOA_PARENT_ID = '62bcec3b-bed4-45c2-9582-83c68bf586ef';
export const HOA_GROUP_ID = 'b373fc51-e22d-45d0-bcdc-7b7ea42e1bf3';
export const HOA_MAIN_PARENT_IDX = 'd0f7f9de-1b9e-4792-895a-604a43759b3f';
export const HOA_OPTION_ID = '228de1ce-c3f5-452f-b2f4-6404ccc4c305';

export const BUYER_SECTION = '18bf5b7b-31d0-4000-89a3-14354c485700';
export const BUYER_GROUP = '18bf5bc4-db50-4000-840b-8f3687f42500';
export const BUYER_PARENT = '18bf5d33-8ba0-4000-8349-86726cf91500';
export const BUYER_PARENT_OPTION = '18bf5d3d-7a60-4000-8be6-9d444703f100';
export const BUYER_INDIVIDUAL_ENTITY = '194f3f72-6350-4000-814f-d71dd8149d00';

export const LOAN_NUMBER_1 = 'e894ca92-a22d-4867-98cd-a4cab955f01c';
export const LOAN_NUMBER_2 = 'b666fd10-5e36-46da-a221-d648d7fa4af1';
export const LENDER_INFO_ID = '85155e4b-7ca8-4829-a9c9-3ce8271cb0cb';
export const LENDER_GROUP = '2d7023f5-6200-49a9-aef0-2b74162006cc';
export const MAIN_LOAN_ELEM = 'f7ed7412-07e3-4b4a-99c4-8ed269c41879';
export const LOAN_2 = '86e017bf-46bf-47ae-bfba-36cfd2f37cec';

export const LOAN_UPLOAD_NAME = 'core_last_statement';
export const LENDER_NAME = 'core_lender_company';
export const LOAN_NUMBER_NAME = 'core_lender_loannum';
export const LENDER_PHONE_NAME = 'core_lender_phone';
export const FIELDS_CHECK_VALIDATIONS = [LENDER_NAME, LOAN_NUMBER_NAME, LENDER_PHONE_NAME];

export const LENDER_INFO_IDX = 2;

export const BORROWER_LENDER_SECTION = '18bf5f2a-c130-4000-873d-e58c37859200';
export const BORROWER_MAIN_PARENT_ID = 'f7ed7412-07e3-4b4a-99c4-8ed269c41879';
export const BORROWER_LOAN_2 = '18bf5fcf-c1b0-4000-8c92-e6bf020eb200';
export const BORROWER_LENDER_GROUP = '18bf5fcc-0670-4000-831a-2d701b8dde00';
export const BORROWER_LENDER_EQUITY_FIELD = 'lender_equity';

export const POA_ID = '8ed55c31-e12c-4e51-aaa9-1cd75c8be0f5';

export const SUPER_ADMIN_EMAIl = 'superadmin@mailinator.com';

export const ID_ONLY_SECTION_ID = '19eb018e-2390-4000-86ee-a3618336cc80';

// CLIEN-208: Realtor role hidden from role selection - portal is disabled.
// To re-enable, add back: { name: 'realtor', label: 'Realtor' },
export const ROLES_FOR_SUPER_ADMIN = [
  { name: 'client admin', label: 'Client Admin' },
  { name: 'client user', label: 'Client User' },
  { name: 'office admin', label: 'Office Admin' },
  { name: 'office user', label: 'Office User' },
  // { name: 'realtor', label: 'Realtor' },
];

export const ROLES_FOR_CLIENT_ADMIN = [
  { name: 'office admin', label: 'Office Admin' },
  { name: 'office user', label: 'Office User' },
  // { name: 'realtor', label: 'Realtor' },
];

export const SSN_FIELDS = [
  'core_ssn',
  'core_spouse_ssn',
  'seller_spouse_ssn',
  'borrower_spouse_ssn',
  'buyer_spouse_ssn',
  'seller_ssn',
  'buyer_ssn',
  'borrower_ssn',
  'borrower_spouse_ssn',
  'borrower_ssn',
  'core_tid',
  'e_core_tax_id',
  'buyer_entity_tax_id',
  'borrower_tid',
  'borrower_core_tax_id',
  'seller_entity_ein',
  'borrower_entity_ein',
  'buyer_entity_ein',
  'seller_entity_ssn',
  'borrower_entity_ssn',
  'buyer_entity_ssn',
  'borrower_entity_member_ssn',
  'buyer_entity_member_ssn',
  'seller_entity_member_ssn',
  'participant_ssn',
];

// Acknowledgment section related constants

export const ACKNOWLEDGMENT_ELEMENTS = ['media_intro', 'buyer_field_90950'];
export const ACKNOWLEDGMENT_SECTION_ID = [
  'a514475f-6e4e-4278-9880-1759462e697f',
  '19a4a917-4c70-4000-85ca-f7fca4ba7380',
];

export const languages = [
  { label: 'Afrikaans', value: 'af' },
  { label: 'Albanian', value: 'sq' },
  { label: 'Amharic', value: 'am' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Armenian', value: 'hy' },
  { label: 'Azerbaijani', value: 'az' },
  { label: 'Basque', value: 'eu' },
  { label: 'Belarusian', value: 'be' },
  { label: 'Bengali', value: 'bn' },
  { label: 'Bosnian', value: 'bs' },
  { label: 'Bulgarian', value: 'bg' },
  { label: 'Catalan', value: 'ca' },
  { label: 'Cebuano', value: 'ceb' },
  { label: 'Chinese (Simplified)', value: 'zh-CN' },
  { label: 'Chinese (Traditional)', value: 'zh-TW' },
  { label: 'Corsican', value: 'co' },
  { label: 'Croatian', value: 'hr' },
  { label: 'Czech', value: 'cs' },
  { label: 'Danish', value: 'da' },
  { label: 'Dutch', value: 'nl' },
  { label: 'English', value: 'en' },
  { label: 'Esperanto', value: 'eo' },
  { label: 'Estonian', value: 'et' },
  { label: 'Finnish', value: 'fi' },
  { label: 'French', value: 'fr' },
  { label: 'Frisian', value: 'fy' },
  { label: 'Galician', value: 'gl' },
  { label: 'Georgian', value: 'ka' },
  { label: 'German', value: 'de' },
  { label: 'Greek', value: 'el' },
  { label: 'Gujarati', value: 'gu' },
  { label: 'Haitian Creole', value: 'ht' },
  { label: 'Hausa', value: 'ha' },
  { label: 'Hawaiian', value: 'haw' },
  { label: 'Hebrew', value: 'he' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Hmong', value: 'hmn' },
  { label: 'Hungarian', value: 'hu' },
  { label: 'Icelandic', value: 'is' },
  { label: 'Igbo', value: 'ig' },
  { label: 'Indonesian', value: 'id' },
  { label: 'Irish', value: 'ga' },
  { label: 'Italian', value: 'it' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Javanese', value: 'jv' },
  { label: 'Kannada', value: 'kn' },
  { label: 'Kazakh', value: 'kk' },
  { label: 'Khmer', value: 'km' },
  { label: 'Kinyarwanda', value: 'rw' },
  { label: 'Korean', value: 'ko' },
  { label: 'Kurdish (Kurmanji)', value: 'ku' },
  { label: 'Kyrgyz', value: 'ky' },
  { label: 'Lao', value: 'lo' },
  { label: 'Latin', value: 'la' },
  { label: 'Latvian', value: 'lv' },
  { label: 'Lithuanian', value: 'lt' },
  { label: 'Luxembourgish', value: 'lb' },
  { label: 'Macedonian', value: 'mk' },
  { label: 'Malagasy', value: 'mg' },
  { label: 'Malay', value: 'ms' },
  { label: 'Malayalam', value: 'ml' },
  { label: 'Maltese', value: 'mt' },
  { label: 'Maori', value: 'mi' },
  { label: 'Marathi', value: 'mr' },
  { label: 'Mongolian', value: 'mn' },
  { label: 'Myanmar (Burmese)', value: 'my' },
  { label: 'Nepali', value: 'ne' },
  { label: 'Norwegian', value: 'no' },
  { label: 'Nyanja (Chichewa)', value: 'ny' },
  { label: 'Odia (Oriya)', value: 'or' },
  { label: 'Pashto', value: 'ps' },
  { label: 'Persian', value: 'fa' },
  { label: 'Polish', value: 'pl' },
  { label: 'Portuguese (Portugal, Brazil)', value: 'pt' },
  { label: 'Punjabi', value: 'pa' },
  { label: 'Romanian', value: 'ro' },
  { label: 'Russian', value: 'ru' },
  { label: 'Samoan', value: 'sm' },
  { label: 'Scots Gaelic', value: 'gd' },
  { label: 'Serbian', value: 'sr' },
  { label: 'Sesotho', value: 'st' },
  { label: 'Shona', value: 'sn' },
  { label: 'Sindhi', value: 'sd' },
  { label: 'Sinhala (Sinhalese)', value: 'si' },
  { label: 'Slovak', value: 'sk' },
  { label: 'Slovenian', value: 'sl' },
  { label: 'Somali', value: 'so' },
  { label: 'Spanish', value: 'es' },
  { label: 'Sundanese', value: 'su' },
  { label: 'Swahili', value: 'sw' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Tagalog (Filipino)', value: 'tl' },
  { label: 'Tajik', value: 'tg' },
  { label: 'Tamil', value: 'ta' },
  { label: 'Tatar', value: 'tt' },
  { label: 'Telugu', value: 'te' },
  { label: 'Thai', value: 'th' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Turkmen', value: 'tk' },
  { label: 'Ukrainian', value: 'uk' },
  { label: 'Urdu', value: 'ur' },
  { label: 'Uyghur', value: 'ug' },
  { label: 'Uzbek', value: 'uz' },
  { label: 'Vietnamese', value: 'vi' },
  { label: 'Welsh', value: 'cy' },
  { label: 'Xhosa', value: 'xh' },
  { label: 'Yiddish', value: 'yi' },
  { label: 'Yoruba', value: 'yo' },
  { label: 'Zulu', value: 'zu' },
];
export const dlSectionIds = [
  'c22663ed-489b-454a-9d63-5b77579b5134',
  'c22663ed-489b-454a-9d63-5b77579b5134',
  '18bf5b7b-31d0-4000-89a3-14354c485700',
];

export const primaryDlFields = ['0421c07b-8c0c-4f0b-82d1-6fe45e2e0007'];

export const DL_FIELD_NUMBER = '_driver_lic';
export const DL_FIELD_STATE = '_driver_lic_state';
export const DL_FIELD_DOB = '_date_of_birth';

export const GROUP_TYPE_IDV = 'idv';
export const IDV_DL_NUMBER_INFIX = 'participant_driver_license_number';
export const IDV_DL_STATE_INFIX = 'participant_driver_license_state';
export const IDV_DL_DOB_INFIX = 'participant_date_of_birth';
export const IDV_SSN = 'participant_ssn';

/** `element_name` fragments for entity trustee/member DL fields (see additionalTrustees.json). */
export const ENTITY_MEMBER_SSN_INFIX = '_entity_member_ssn_';
export const ENTITY_MEMBER_DL_LICENSE_INFIX = '_entity_member_driver_lic_';
export const ENTITY_MEMBER_DL_STATE_INFIX = '_entity_member_driver_lic_state_';
export const ENTITY_MEMBER_DL_DOB_INFIX = '_entity_member_date_of_birth_';
export const ENTITY_MEMBER_FIRST_NAME_INFIX = '_entity_member_first_name_';
export const ENTITY_MEMBER_LAST_NAME_INFIX = '_entity_member_last_name_';
export const ENTITY_MEMBER_EMAIL_INFIX = '_entity_member_email_';
export const ENTITY_MEMBER_PHONE_INFIX = '_entity_member_phone_';
export const ENTITY_MEMBER_GROUP_ID = '19eac0ad-4ac0-4000-8c19-7be2a3ffcd00';
/** data-field-id on member/primary DL upload upload UI for scroll + highlight */
export const DL_UPLOAD_BUTTON_FIELD_ID = 'dl-upload-button';

export const ADDITIONAL_SELLER_TYPE = '_type';
export const ADDITIONAL_SELLER_FIRST_NAME = '_first_name';
export const ADDITIONAL_SELLER_LAST_NAME = '_last_name';
export const ADDITIONAL_SELLER_MIDDLE_NAME = '_middle_name';
export const ADDITIONAL_SELLER_ENTITY_FIRST_NAME = '_entity_contact_first_name';
export const ADDITIONAL_SELLER_ENTITY_LAST_NAME = '_entity_contact_last_name';
export const ADDITIONAL_SELLER_ENTITY_MIDDLE_NAME = '_entity_contact_middle_name';
export const ADDITIONAL_SELLER_ENTITY_BUSINESS_NAME = '_entity_business_name';
export const ENTITY_IS_EIN = 'entity_ssn_is_ein';
export const BORROWER_ENTITY_IS_EIN = 'borrower_entity_ssn_is_ein';
export const ADDITIONAL_SELLER_ENTITY_IS_EIN = '_entity_ssn_is_ein';
export const LENDER_EQUITY_FIELD = 'lender_equity';

export const DL_NUMBER_FIELD = '0421c07b-8c0c-4f0b-82d1-6fe45e2e0007';
export const DL_STATE_FIELD = '0104abf4-2633-47c9-a217-62c6f476bb6b';
export const BUYER_DL_STATE_FIELD = '194f39a5-a770-4000-832d-b43f09378400';
export const DATE_OF_BIRTH = '7d77a9a9-6365-4882-839a-a8566b9bb045';
export const BUYER_BATE_OF_BIRTH = '18bf5d1d-7480-4000-8a4b-e03bedf32e00';
export const SELLER_FIRST_NAME = 'e1a08d42-7169-440d-89e9-cc1649033804';
export const SELLER_MIDDLE_NAME = 'dc7299e2-8273-48dc-9b91-13f7ad4d4ab1';
export const BORROWER_FIRST_NAME = 'e1a08d42-7169-440d-89e9-cc1649033804';
export const SELLER_LAST_NAME = '8c1ae17a-258e-4bbd-b7cf-b5a30df43e2f';
export const BORROWER_LAST_NAME = '8c1ae17a-258e-4bbd-b7cf-b5a30df43e2f';
export const BUYER_FULL_NAME_ID = '18bf5cb5-0580-4000-8859-a024f878d100';
export const SIGNER_FULL_NAME = '2b81107c-a132-4914-a1cd-0178e6638ac7';
export const ENTITY_LEGAL_NAME = '72df5d94-f5f8-4eda-b109-407634bd0831';
export const BORROWER_MIDDLE_NAME = 'dc7299e2-8273-48dc-9b91-13f7ad4d4ab1';

export const BUYER_FIRST_NAME = '18bf5cb5-0580-4000-8859-a024f878d100';
export const BUYER_MIDDLE_NAME = '19bc006f-1f80-4000-836c-e0de97006980';
export const BUYER_LAST_NAME = '19bc006f-f810-4000-8ec3-81a5af9eda80';

export const IS_INDIVIDUAL_ENTITY = '02a56cad-f7ae-445c-996a-ac7dabbfa495';
export const BUYER_ENTITY_TYPE = '194f36ef-1880-4000-840e-ca2ea180e300';
export const ENTITY_TYPE = '19e45f77-fc92-4ad8-b971-b79de5265dbf';

export const MAILING_ADDRESS_QUESTION = '8d961be3-a84d-40b2-998c-d052dbc91415';
export const MAILING_ADDRESS_ADDRESS = '73fdef5b-54b0-4304-b3a7-9f365acccfb0';
export const MAILING_ADDRESS_CITY = 'de1c982f-1ba8-4795-aeb6-d4a341b2aa92';
export const MAILING_ADDRESS_STATE = '49a30dc5-65ab-4f66-9887-2a6f9ef80860';
export const MAILING_ADDRESS_ZIP = '80e2ba9b-6236-4ed1-a5da-e6b1460169c7';

export const BUYER_CURRENT_MAILING_ADDRESS = '19bc0157-c990-4000-8aff-abbf55913f00';

export const ATTENTION_TO_NAME = 'core_mail_attn';

export const DL_VERIFICATION_FIELDS = [
  DL_NUMBER_FIELD,
  DL_STATE_FIELD,
  SELLER_FIRST_NAME,
  SELLER_LAST_NAME,
  MAILING_ADDRESS_ADDRESS,
  MAILING_ADDRESS_CITY,
  MAILING_ADDRESS_STATE,
  MAILING_ADDRESS_ZIP,
  DATE_OF_BIRTH,
  BUYER_BATE_OF_BIRTH,
  BUYER_DL_STATE_FIELD,
];

export const STATE_FIELDS = [
  '0104abf4-2633-47c9-a217-62c6f476bb6b',
  '3c87f997-50fd-4139-8cf0-498423bccfba',
  '49a30dc5-65ab-4f66-9887-2a6f9ef80860',
  'e4bfa203-2bff-49a0-b8c3-f19bd4a59d9',
  '59bd2867-91e8-46f1-aa3e-56cb2d85abb0',
  '889dd184-3d56-4cce-970c-ca090f4d76b6',
  'e5cfca34-20a4-46aa-a9b0-466cfc591d42',
  '15feaabd-250e-40a3-b667-7e8990ff9e1f',
  'a6e5bbe6-46fd-4bf3-80b3-70c90f11d9e7',
];

// seller disbursement fields

export const SELLER_BEST_OF_KNOWLEDGE_FIELD = 'seller_best_of_knowledge';
export const SELLER_PERCENTAGE_FIELD = 'seller_percentage_proceeds';
export const SELLER_SPLIT_TYPE_FIELD = 'seller_proceeds_division';
export const SELLER_AGREEMENT_FIELD = 'seller_agree_with_division';

// Spouse feature (CLIEN-308) - "Is the additional seller listed your spouse?" radio, one per
// additional seller (seller_is_spouse_2, _3, ...).
export const SELLER_IS_SPOUSE_FIELD_PREFIX = 'seller_is_spouse_';
// Primary seller's own "My spouse is a Seller on this transaction" checkbox.
export const SELLER_MY_SPOUSE_CHECKBOX_1 = 'seller_my_spouse_checkbox_1';
// Primary seller's own Marital Status - seller_is_spouse_N only applies when this is "Married".
export const SELLER_MARITAL_STATUS_FIELD = 'seller_marital_status';
// Spouse details group - shown only when the seller is Married and no additional seller is their spouse. TODO: replace temp id.
export const SELLER_SPOUSE_GROUP_ID = '1a185e99-8ed0-4000-866f-6bc8bff17500';

export const CLOSING_INFO_GROUP_ID = '18c85986-6cc0-4000-8e56-659d49645000';
export const SELLER_SHARED_GROUP_ID = '19a48530-4300-4000-854d-5cbd2306ec80';

export const INDENTED_FIELDS = ['seller_field_84498', 'seller_field_43869', 'seller_field_78391'];

export const sampleDataSetCSV = () => [
  [
    {
      column: 'Escrow Number',
      value: '123-Escrow',
      name: 'escrow_number',
    },
    {
      column: 'Anticipated Close Date',
      value: '09/13/2024',
      name: 'anticipated_close_date',
    },
    {
      column: 'Property Address Line 1',
      value: '123 Main Street',
      name: 'property_address_1',
    },
    {
      column: 'Property Address Line 2',
      value: '',
      name: 'property_address_2',
    },
    {
      column: 'Property Unit Number',
      value: '',
      name: 'property_unit_no',
    },
    {
      column: 'Property City',
      value: 'Irvine',
      name: 'property_city',
    },
    {
      column: 'Property State',
      value: 'Ca',
      name: 'property_state',
    },
    {
      column: 'Property ZIP Code',
      value: 92603,
      name: 'property_zipcode',
    },
    {
      column: 'Property County',
      value: 'Orange',
      name: 'property_county',
    },
    {
      column: 'Number of participant',
      value: 3,
      name: 'no_of_participants',
    },
    {
      column: 'Multiple Participant Form',
      value: 'Separate',
    },
    {
      column: 'Participant1 Category',
      value: 'individual',
    },
    {
      column: 'Participant1 Type',
      value: 'seller',
    },
    {
      column: 'Participant1 First Name',
      value: 'John',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Middle Name',
      value: 'A',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Last Name',
      value: 'Doe',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant1 Email',
      value: 'john.doe@mailinator.com',
      name: 'email',
    },
    {
      column: 'Participant1 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant1 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant1 routing number',
      value: '122199983',
      name: 'routing_number',
    },
    {
      column: 'Participant1 bank account number',
      value: '923305129348',
      name: 'bank_account_number',
    },
    {
      column: 'Participant1 name on account',
      value: 'John Doe',
      name: 'name_on_account',
    },
    {
      column: 'Participant1 mailing address same as property address',
      value: 'yes',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant1 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant1 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant1 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant1 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant1 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant1 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant1 mailing County',
      value: '',
      name: 'county',
    },
    {
      column: 'Participant2 Category',
      value: 'individual',
    },
    {
      column: 'Participant2 Type',
      value: 'seller',
    },
    {
      column: 'Participant2 First Name',
      value: 'Maria',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Middle Name',
      value: 'B',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Last Name',
      value: 'Barbis',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant2 Email',
      value: 'maria@mailinator.com',
      name: 'email',
    },
    {
      column: 'Participant2 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant2 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant2 routing number',
      value: '122199983',
      name: 'routing_number',
    },
    {
      column: 'Participant2 bank account number',
      value: '923305129348',
      name: 'bank_account_number',
    },
    {
      column: 'Participant2 name on account',
      value: 'John Doe',
      name: 'name_on_account',
    },
    {
      column: 'Participant2 mailing address same as property address',
      value: 'yes',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant2 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant2 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant2 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant2 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant2 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant2 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant2 mailing County',
      value: '',
      name: 'county',
    },
    {
      column: 'Participant3 Category',
      value: 'individual',
    },
    {
      column: 'Participant3 Type',
      value: 'buyer',
    },
    {
      column: 'Participant3 First Name',
      value: 'John',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Middle Name',
      value: 'C',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Last Name',
      value: 'Carter',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant3 Email',
      value: 'john.carter@mailinator.com',
      name: 'email',
    },
    {
      column: 'Participant3 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant3 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant3 routing number',
      value: '122199983',
      name: 'routing_number',
    },
    {
      column: 'Participant3 bank account number',
      value: '923305129348',
      name: 'bank_account_number',
    },
    {
      column: 'Participant3 name on account',
      value: 'John Doe',
      name: 'name_on_account',
    },
    {
      column: 'Participant3 mailing address same as property address',
      value: 'yes',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant3 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant3 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant3 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant3 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant3 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant3 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant3 mailing County',
      value: '',
      name: 'county',
    },
  ],
  [
    {
      column: 'Escrow Number',
      value: '1235-Escrow',
      name: 'escrow_number',
    },
    {
      column: 'Anticipated Close Date',
      value: '09/13/2024',
      name: 'anticipated_close_date',
    },
    {
      column: 'Property Address Line 1',
      value: '123 Main Street',
      name: 'property_address_1',
    },
    {
      column: 'Property Address Line 2',
      value: '',
      name: 'property_address_2',
    },
    {
      column: 'Property Unit Number',
      value: '',
      name: 'property_unit_no',
    },
    {
      column: 'Property City',
      value: 'Bennington',
      name: 'property_city',
    },
    {
      column: 'Property State',
      value: 'Ne',
      name: 'property_state',
    },
    {
      column: 'Property ZIP Code',
      value: 68007,
      name: 'property_zipcode',
    },
    {
      column: 'Property County',
      value: 'Douglas',
      name: 'property_county',
    },
    {
      column: 'Number of participant',
      value: 2,
      name: 'no_of_participants',
    },
    {
      column: 'Multiple Participant Form',
      value: 'Single',
      name: 'multiple_participant_form',
    },
    {
      column: 'Participant1 Category',
      value: 'entity',
    },
    {
      column: 'Participant1 Type',
      value: 'seller',
    },
    {
      column: 'Participant1 First Name',
      value: '',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Middle Name',
      value: '',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Last Name',
      value: '',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant1 Email',
      value: 'clientverify.corp@mailinator.com',
      name: 'email',
    },
    {
      column: 'Participant1 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant1 Legal Business Name',
      value: 'ClientVerify',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity Type',
      value: 'Corporation',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity State',
      value: 'CA',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant1 mailing address same as property address',
      value: 'no',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant1 mailing Address line 1',
      value: '1234 Main Street',
      name: 'address_line_1',
    },
    {
      column: 'Participant1 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant1 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant1 mailing City',
      value: 'Murrieta',
      name: 'city',
    },
    {
      column: 'Participant1 mailing State',
      value: 'Ca',
      name: 'state',
    },
    {
      column: 'Participant1 mailing ZIP Code',
      value: 92562,
      name: 'zip_code',
    },
    {
      column: 'Participant1 mailing County',
      value: '',
      name: 'county',
    },
    {
      column: 'Participant2 Category',
      value: 'entity',
    },
    {
      column: 'Participant2 Type',
      value: 'seller',
    },
    {
      column: 'Participant2 First Name',
      value: '',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Middle Name',
      value: '',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Last Name',
      value: '',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant2 Email',
      value: 'tek.corp@mailinator.com',
      name: 'email',
    },
    {
      column: 'Participant2 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant2 Legal Business Name',
      value: 'Tek corporation',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity Type',
      value: 'Corporation',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity State',
      value: 'CA',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant2 mailing address same as property address',
      value: 'no',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant2 mailing Address line 1',
      value: '12 Main Street',
      name: 'address_line_1',
    },
    {
      column: 'Participant2 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant2 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant2 mailing City',
      value: 'Murrieta',
      name: 'city',
    },
    {
      column: 'Participant2 mailing State',
      value: 'Ca',
      name: 'state',
    },
    {
      column: 'Participant2 mailing ZIP Code',
      value: 92562,
      name: 'zip_code',
    },
    {
      column: 'Participant2 mailing County',
      value: '',
      name: 'county',
    },
    {
      column: 'Participant3 Category',
      value: '',
    },
    {
      column: 'Participant3 Type',
      value: '',
    },
    {
      column: 'Participant3 First Name',
      value: '',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Middle Name',
      value: '',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Last Name',
      value: '',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Social Security Number',
      value: '',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant3 Email',
      value: '',
      name: 'email',
    },
    {
      column: 'Participant3 Phone',
      value: '',
      name: 'mobile_phone',
    },
    {
      column: 'Participant3 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant3 mailing address same as property address',
      value: '',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant3 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant3 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant3 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant3 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant3 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant3 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant3 mailing County',
      value: '',
      name: 'county',
    },
  ],
  [
    {
      column: 'Escrow Number',
      value: '1232-Escrow',
      name: 'escrow_number',
    },
    {
      column: 'Anticipated Close Date',
      value: '09/13/2024',
      name: 'anticipated_close_date',
    },
    {
      column: 'Property Address Line 1',
      value: '123 Main Street',
      name: 'property_address_1',
    },
    {
      column: 'Property Address Line 2',
      value: '',
      name: 'property_address_2',
    },
    {
      column: 'Property Unit Number',
      value: '',
      name: 'property_unit_no',
    },
    {
      column: 'Property City',
      value: 'Irvine',
      name: 'property_city',
    },
    {
      column: 'Property State',
      value: 'Ca',
      name: 'property_state',
    },
    {
      column: 'Property ZIP Code',
      value: 92603,
      name: 'property_zipcode',
    },
    {
      column: 'Property County',
      value: 'Orange',
      name: 'property_county',
    },
    {
      column: 'Number of participant',
      value: 1,
      name: 'no_of_participants',
    },
    {
      column: 'Multiple Participant Form',
      value: 'Single',
      name: 'multiple_participant_form',
    },
    {
      column: 'Participant1 Category',
      value: 'individual',
    },
    {
      column: 'Participant1 Type',
      value: 'borrower',
    },
    {
      column: 'Participant1 First Name',
      value: 'John',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Middle Name',
      value: 'A',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Last Name',
      value: 'Carter',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Social Security Number',
      value: '111-11-2222',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant1 Email',
      value: 'john.carter@gmail.com',
      name: 'email',
    },
    {
      column: 'Participant1 Phone',
      value: '(111) 111-2222',
      name: 'mobile_phone',
    },
    {
      column: 'Participant1 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant1 mailing address same as property address',
      value: 'no',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant1 mailing Address line 1',
      value: '23355 Bishop Rd',
      name: 'address_line_1',
    },
    {
      column: 'Participant1 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant1 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant1 mailing City',
      value: 'Murrieta',
      name: 'city',
    },
    {
      column: 'Participant1 mailing State',
      value: 'Ca',
      name: 'state',
    },
    {
      column: 'Participant1 mailing ZIP Code',
      value: 92562,
      name: 'zip_code',
    },
    {
      column: 'Participant1 mailing County',
      value: '',
      name: 'county',
    },
    {
      column: 'Participant2 Category',
      value: '',
    },
    {
      column: 'Participant2 Type',
      value: '',
    },
    {
      column: 'Participant2 First Name',
      value: '',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Middle Name',
      value: '',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Last Name',
      value: '',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant2 Social Security Number',
      value: '',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant2 Email',
      value: '',
      name: 'email',
    },
    {
      column: 'Participant2 Phone',
      value: '',
      name: 'mobile_phone',
    },
    {
      column: 'Participant2 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant2 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant2 mailing address same as property address',
      value: '',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant2 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant2 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant2 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant2 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant2 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant2 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant2 mailing County',
      value: '',
      name: 'county',
    },
    {
      column: 'Participant3 Category',
      value: '',
    },
    {
      column: 'Participant3 Type',
      value: '',
    },
    {
      column: 'Participant3 First Name',
      value: '',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Middle Name',
      value: '',
      name: 'middle_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Last Name',
      value: '',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant3 Social Security Number',
      value: '',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant3 Email',
      value: '',
      name: 'email',
    },
    {
      column: 'Participant3 Phone',
      value: '',
      name: 'mobile_phone',
    },
    {
      column: 'Participant3 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant3 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant3 mailing address same as property address',
      value: '',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant3 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant3 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant3 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant3 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant3 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant3 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant3 mailing County',
      value: '',
      name: 'county',
    },
  ],
];

export const sampleDataSetCSVPhh = () => [
  [
    {
      column: 'Loan Skey',
      value: '123-Escrow',
      name: 'escrow_number',
    },
    {
      column: 'Anticipated Close Date',
      value: '09/13/2024',
      name: 'anticipated_close_date',
    },
    {
      column: 'Property Address Line 1',
      value: '123 Main Street',
      name: 'property_address_1',
    },
    {
      column: 'Property Address Line 2',
      value: '',
      name: 'property_address_2',
    },
    {
      column: 'Property Unit Number',
      value: '',
      name: 'property_unit_no',
    },
    {
      column: 'Property City',
      value: 'Irvine',
      name: 'property_city',
    },
    {
      column: 'Property State',
      value: 'Ca',
      name: 'property_state',
    },
    {
      column: 'Property ZIP Code',
      value: 92603,
      name: 'property_zipcode',
    },
    {
      column: 'Property County',
      value: 'Orange',
      name: 'property_county',
    },
    {
      column: 'Number of participant',
      value: 1,
      name: 'no_of_participants',
    },
    {
      column: 'Participant1 Category',
      value: 'individual',
    },
    {
      column: 'Participant1 Type',
      value: 'borrower',
    },

    {
      column: 'Participant1 routing number',
      value: '122199983',
      name: 'routing_number',
    },
    {
      column: 'Participant1 bank account number',
      value: '923305129348',
      name: 'bank_account_number',
    },
    {
      column: 'Participant1 name on account',
      value: 'John Doe',
      name: 'name_on_account',
    },
    {
      column: 'Participant1 First Name',
      value: 'John',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Last Name',
      value: 'Doe',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant1 Email',
      value: 'john@doe.com',
      name: 'email',
    },
    {
      column: 'Participant1 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant1 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant1 mailing address same as property address',
      value: 'yes',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant1 mailing Address line 1',
      value: '',
      name: 'address_line_1',
    },
    {
      column: 'Participant1 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant1 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant1 mailing City',
      value: '',
      name: 'city',
    },
    {
      column: 'Participant1 mailing State',
      value: '',
      name: 'state',
    },
    {
      column: 'Participant1 mailing ZIP Code',
      value: '',
      name: 'zip_code',
    },
    {
      column: 'Participant1 mailing County',
      value: '',
      name: 'county',
    },
  ],
  [
    {
      column: 'Loan Skey',
      value: '1232-Escrow',
      name: 'escrow_number',
    },
    {
      column: 'Anticipated Close Date',
      value: '09/13/2024',
      name: 'anticipated_close_date',
    },
    {
      column: 'Property Address Line 1',
      value: '123 Main Street',
      name: 'property_address_1',
    },
    {
      column: 'Property Address Line 2',
      value: '',
      name: 'property_address_2',
    },
    {
      column: 'Property Unit Number',
      value: '',
      name: 'property_unit_no',
    },
    {
      column: 'Property City',
      value: 'Irvine',
      name: 'property_city',
    },
    {
      column: 'Property State',
      value: 'Ca',
      name: 'property_state',
    },
    {
      column: 'Property ZIP Code',
      value: 92603,
      name: 'property_zipcode',
    },
    {
      column: 'Property County',
      value: 'Orange',
      name: 'property_county',
    },
    {
      column: 'Number of participant',
      value: 1,
      name: 'no_of_participants',
    },
    {
      column: 'Participant1 Category',
      value: 'individual',
    },
    {
      column: 'Participant1 Type',
      value: 'borrower',
    },
    {
      column: 'Participant1 routing number',
      value: '122199983',
      name: 'routing_number',
    },
    {
      column: 'Participant1 bank account number',
      value: '923305129348',
      name: 'bank_account_number',
    },
    {
      column: 'Participant1 name on account',
      value: 'John Carter',
      name: 'name_on_account',
    },
    {
      column: 'Participant1 First Name',
      value: 'John',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Last Name',
      value: 'Carter',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Social Security Number',
      value: '111-11-2222',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant1 Email',
      value: 'john@carter.com',
      name: 'email',
    },
    {
      column: 'Participant1 Phone',
      value: '(111) 111-2222',
      name: 'mobile_phone',
    },
    {
      column: 'Participant1 Legal Business Name',
      value: '',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity Type',
      value: '',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity State',
      value: '',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant1 mailing address same as property address',
      value: 'no',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant1 mailing Address line 1',
      value: '1234 Main Street',
      name: 'address_line_1',
    },
    {
      column: 'Participant1 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant1 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant1 mailing City',
      value: 'Murrieta',
      name: 'city',
    },
    {
      column: 'Participant1 mailing State',
      value: 'Ca',
      name: 'state',
    },
    {
      column: 'Participant1 mailing ZIP Code',
      value: 92562,
      name: 'zip_code',
    },
    {
      column: 'Participant1 mailing County',
      value: '',
      name: 'county',
    },
  ],
  [
    {
      column: 'Loan Skey',
      value: '1235-Escrow',
      name: 'escrow_number',
    },
    {
      column: 'Anticipated Close Date',
      value: '09/13/2024',
      name: 'anticipated_close_date',
    },
    {
      column: 'Property Address Line 1',
      value: '123 Main Street',
      name: 'property_address_1',
    },
    {
      column: 'Property Address Line 2',
      value: '',
      name: 'property_address_2',
    },
    {
      column: 'Property Unit Number',
      value: '',
      name: 'property_unit_no',
    },
    {
      column: 'Property City',
      value: 'Bennington',
      name: 'property_city',
    },
    {
      column: 'Property State',
      value: 'Ne',
      name: 'property_state',
    },
    {
      column: 'Property ZIP Code',
      value: 68007,
      name: 'property_zipcode',
    },
    {
      column: 'Property County',
      value: 'Douglas',
      name: 'property_county',
    },
    {
      column: 'Number of participant',
      value: 1,
      name: 'no_of_participants',
    },
    {
      column: 'Participant1 Category',
      value: 'entity',
    },
    {
      column: 'Participant1 Type',
      value: 'borrower',
    },
    {
      column: 'Participant1 routing number',
      value: '122199983',
      name: 'routing_number',
    },
    {
      column: 'Participant1 bank account number',
      value: '923305129348',
      name: 'bank_account_number',
    },
    {
      column: 'Participant1 name on account',
      value: 'Steaven Smith',
      name: 'name_on_account',
    },
    {
      column: 'Participant1 First Name',
      value: '',
      name: 'first_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Last Name',
      value: '',
      name: 'last_name',
      category: 'individual',
    },
    {
      column: 'Participant1 Social Security Number',
      value: '111-11-1111',
      name: 'ssn_number',
      category: 'individual',
    },
    {
      column: 'Participant1 Email',
      value: 'entity@email.com',
      name: 'email',
    },
    {
      column: 'Participant1 Phone',
      value: '(111) 111-1111',
      name: 'mobile_phone',
    },
    {
      column: 'Participant1 Legal Business Name',
      value: 'ClientVerify',
      name: 'business_name',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity Type',
      value: 'Corporation',
      name: 'entity_type',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity State',
      value: 'CA',
      name: 'entity_state',
      category: 'entity',
    },
    {
      column: 'Participant1 Entity EIN',
      value: '',
      name: 'entity_ein',
      category: 'entity',
    },
    {
      column: 'Participant1 mailing address same as property address',
      value: 'no',
      name: 'is_address_same_as_order_property',
    },
    {
      column: 'Participant1 mailing Address line 1',
      value: '1234 Main Street',
      name: 'address_line_1',
    },
    {
      column: 'Participant1 mailing Address line 2',
      value: '',
      name: 'address_line_2',
    },
    {
      column: 'Participant1 mailing Unit Number',
      value: '',
      name: 'unit_no',
    },
    {
      column: 'Participant1 mailing City',
      value: 'Murrieta',
      name: 'city',
    },
    {
      column: 'Participant1 mailing State',
      value: 'Ca',
      name: 'state',
    },
    {
      column: 'Participant1 mailing ZIP Code',
      value: 92562,
      name: 'zip_code',
    },
    {
      column: 'Participant1 mailing County',
      value: '',
      name: 'county',
    },
  ],
];

export const guideTableData = () => [
  {
    column_name: 'Escrow Number',
    possible_values: 'Unique string for each order.',
    required: 'Required',
    example: 'VSS-21231231',
    name: 'escrow_number',
  },
  {
    column_name: 'Anticipated Close Date',
    possible_values: 'Date in the "MM/DD/YYYY" format.',
    required: 'Required',
    example: '09/31/2024',
    name: 'anticipated_close_date',
  },
  {
    column_name: 'Property Address Line 1',
    possible_values: 'Street number and route of the property address.',
    required: 'Required',
    example: '123 Main Street',
    name: 'property_address_1',
  },
  {
    column_name: 'Property Address Line 2',
    possible_values: 'Other details for your property address.',
    required: 'Required',
    example: 'Near Central Park',
    name: 'property_address_2',
  },
  {
    column_name: 'Property Unit Number',
    possible_values: 'Unit number for the property address, if applicable.',
    required: 'Optional',
    example: 'A',
    name: 'property_unit_no',
  },
  {
    column_name: 'Property City',
    possible_values: 'City for the property address.',
    required: 'Required',
    example: 'Irvine',
    name: 'property_city',
  },
  {
    column_name: 'Property State',
    possible_values: 'State for the property address.',
    required: 'Required',
    example: 'CA',
    name: 'property_state',
  },
  {
    column_name: 'Property ZIP Code',
    possible_values: 'ZIP code for the property address.',
    required: 'Required',
    example: '92603',
    name: 'property_zipcode',
  },
  {
    column_name: 'Property County',
    possible_values: 'County for the property address.',
    required: 'Required',
    example: 'Orange',
    name: 'property_county',
  },
  {
    column_name: 'Number of participant',
    possible_values:
      'Number of participants for the transaction. For example, if only the seller is included, it should be 1. If both the buyer and seller are included, it should be 2.',
    required: 'Required',
    example: '1',
    name: 'no_of_participants',
  },
  {
    column_name: 'Multiple Participant Form',
    possible_values: (
      <div>
        <b>Separate</b>(
        <i>
          In case of multiple participants of same participant type, completely separate form will
          be sent to each seller included in the order. The primary seller will no longer be able to
          add new sellers from the participant form. If they want to add a new seller, they can
          contact an admin user, who can add it from the order details screen.
        </i>
        ) or <b>Single</b>(
        <i>
          In case of multiple participants of same participant type, the primary seller completes
          the form first. Then, additional sellers will receive this partially completed
          form(completed by the primary seller) and can continue filling in their details, such as
          uploading a Government Issued ID and adding bank information.
        </i>
        )
      </div>
    ),
    required: 'Required if multiple seller is included in order',
    example: 'Separate',
  },
  {
    column_name: 'Participant1 Category',
    possible_values: 'Either "individual" or "entity".',
    required: 'Required',
    example: 'Individual',
  },
  {
    column_name: 'Participant1 Type',
    possible_values: 'Value from "seller" or "buyer" or "borrower".',
    required: 'Required',
    example: 'seller',
  },
  {
    column_name: 'Participant1 First Name',
    possible_values: 'First name.',
    required: 'If participant category is "Individual", then Yes; otherwise No',
    example: 'John',
    name: 'first_name',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Middle Name',
    possible_values: 'Middle name.',
    required: 'Optional',
    example: 'A',
    name: 'middle_name',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Last Name',
    possible_values: 'Last name.',
    required: 'If participant category is "Individual", then Yes; otherwise No',
    example: 'Doe',
    name: 'last_name',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Social Security Number',
    possible_values:
      'Social security number (Only applicable if participant category is "Individual").',
    required: 'Optional',
    example: '111-11-1111',
    name: 'ssn_number',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Email',
    possible_values: 'Valid email address.',
    required: 'Required',
    example: 'john.doe@gmail.com',
    name: 'email',
  },
  {
    column_name: 'Participant1 Phone',
    possible_values: 'Valid phone number.',
    required: 'Optional',
    example: '(111) 111-2222',
    name: 'mobile_phone',
  },
  {
    column_name: 'Participant1 Legal Business Name',
    possible_values: 'Legal business name (Only applicable if participant category is "Entity").',
    required: 'If participant category is "Entity", then Yes; otherwise No',
    example: 'Tech Corporation',
    name: 'business_name',
    category: 'entity',
  },
  {
    column_name: 'Participant1 Entity Type',
    possible_values:
      'Value from "Corporation", "LLC", "Trust", "Partnership", or "Other" (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'Corporation',
    name: 'entity_type',
    category: 'entity',
  },
  {
    column_name: 'Participant1 Entity State',
    possible_values:
      'State abbreviation for the entity (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'CA',
    name: 'entity_state',
    category: 'entity',
  },
  {
    column_name: 'Participant1 Entity EIN',
    possible_values:
      'Employer Identification Number (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: '11-1111111',
    name: 'entity_ein',
    category: 'entity',
  },
  {
    column_name: 'Participant1 routing number',
    possible_values: 'Routing number for your bank account.',
    required: 'Required',
    example: '122199983',
    name: 'routing_number',
  },
  {
    column_name: 'Participant1 bank account number',
    possible_values: 'Account number for your bank account.',
    required: 'Required',
    example: '923305129348',
    name: 'bank_account_number',
  },
  {
    column_name: 'Participant1 name on account',
    possible_values: 'Name on account for your bank account.',
    required: 'Required',
    example: 'John Doe',
    name: 'name_on_account',
  },
  {
    column_name: 'Participant1 mailing address same as property address',
    possible_values:
      'Either "yes" or "no"(If this is selected as "Yes" you can skip adding participant mailing address details).',
    required: 'Required',
    example: 'yes',
    name: 'is_address_same_as_order_property',
  },
  {
    column_name: 'Participant1 mailing Address line 1',
    possible_values: 'Street number and route for the participant’s address.',
    required: 'Required',
    example: '1233 Main Street',
    name: 'address_line_1',
  },
  {
    column_name: 'Participant1 mailing Address line 2',
    possible_values: 'Mention other details about the address.',
    required: 'Optional',
    example: 'Near Central Park',
    name: 'address_line_2',
  },
  {
    column_name: 'Participant1 mailing Unit Number',
    possible_values: 'Unit number for the participant’s address.',
    required: 'Optional',
    example: 'A',
    name: 'unit_no',
  },
  {
    column_name: 'Participant1 mailing City',
    possible_values: 'City for the participant’s address.',
    required: 'Required',
    example: 'Irvine',
    name: 'city',
  },
  {
    column_name: 'Participant1 mailing State',
    possible_values: 'State for the participant’s address.',
    required: 'Required',
    example: 'CA',
    name: 'state',
  },
  {
    column_name: 'Participant1 mailing ZIP Code',
    possible_values: 'ZIP code for the participant’s address.',
    required: 'Required',
    example: '92603',
    name: 'zip_code',
  },
  {
    column_name: 'Participant1 mailing County',
    possible_values: 'County for the participant’s address.',
    required: 'Optional',
    example: 'Orange',
    name: 'county',
  },
  {
    column_name: 'Participant2 Category',
    possible_values: 'Either "individual" or "entity".',
    required: 'Required',
    example: 'Individual',
  },
  {
    column_name: 'Participant2 Type',
    possible_values: 'Value from "seller" or "buyer" or "borrower".',
    required: 'Required',
    example: 'seller',
  },
  {
    column_name: 'Participant2 First Name',
    possible_values: 'First name.',
    required: 'If participant category is "Individual", then Yes; otherwise No',
    example: 'Steve',
    name: 'first_name',
    category: 'individual',
  },
  {
    column_name: 'Participant2 Middle Name',
    possible_values: 'Middle name.',
    required: 'Optional',
    example: 'Alan',
    name: 'middle_name',
    category: 'individual',
  },
  {
    column_name: 'Participant2 Last Name',
    possible_values: 'Last name.',
    required: 'If participant category is "Individual", then Yes; otherwise No',
    example: 'Smith',
    name: 'last_name',
    category: 'individual',
  },
  {
    column_name: 'Participant2 Social Security Number',
    possible_values:
      'Social security number (Only applicable if participant category is "Individual").',
    required: 'Optional',
    example: '111-11-1111',
    name: 'ssn_number',
    category: 'individual',
  },
  {
    column_name: 'Participant2 Email',
    possible_values: 'Valid email address.',
    required: 'Required',
    example: 'steave.smith@gmail.com',
    name: 'email',
  },
  {
    column_name: 'Participant2 Phone',
    possible_values: 'Valid phone number.',
    required: 'Optional',
    example: '(111) 111-2222',
    name: 'mobile_phone',
  },
  {
    column_name: 'Participant2 Legal Business Name',
    possible_values: 'Legal business name (Only applicable if participant category is "Entity").',
    required: 'If participant category is "Entity", then Yes; otherwise No',
    example: 'Tech Corporation',
    name: 'business_name',
    category: 'entity',
  },
  {
    column_name: 'Participant2 Entity Type',
    possible_values:
      'Value from "Corporation", "LLC", "Trust", "Partnership", or "Other" (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'Corporation',
    name: 'entity_type',
    category: 'entity',
  },
  {
    column_name: 'Participant2 Entity State',
    possible_values:
      'State abbreviation for the entity (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'CA',
    name: 'entity_state',
    category: 'entity',
  },
  {
    column_name: 'Participant2 Entity EIN',
    possible_values:
      'Employer Identification Number (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: '11-1111111',
    name: 'entity_ein',
    category: 'entity',
  },

  {
    column_name: 'Participant2 routing number',
    possible_values: 'Routing number for your bank account.',
    required: 'Required',
    example: '122199983',
    name: 'routing_number',
  },
  {
    column_name: 'Participant2 bank account number',
    possible_values: 'Account number for your bank account.',
    required: 'Required',
    example: '923305129348',
    name: 'bank_account_number',
  },
  {
    column_name: 'Participant2 name on account',
    possible_values: 'Name on account for your bank account.',
    required: 'Required',
    example: 'John Doe',
    name: 'name_on_account',
  },
  {
    column_name: 'Participant2 mailing address same as property address',
    possible_values:
      'Either "yes" or "no"(If this is selected as "Yes" you can skip adding participant mailing address details).',
    required: 'Required',
    example: 'yes',
    name: 'is_address_same_as_order_property',
  },
  {
    column_name: 'Participant2 mailing Address line 1',
    possible_values: 'Street number and route for the participant’s address.',
    required: 'Required',
    example: '1233 ABC Street',
    name: 'address_line_1',
  },
  {
    column_name: 'Participant2 mailing Address line 2',
    possible_values: 'Mention other details about the address.',
    required: 'Optional',
    example: 'Near Central Park',
    name: 'address_line_2',
  },
  {
    column_name: 'Participant2 mailing Unit Number',
    possible_values: 'Unit number for the participant’s address.',
    required: 'Optional',
    example: 'A',
    name: 'unit_no',
  },
  {
    column_name: 'Participant2 mailing City',
    possible_values: 'City for the participant’s address.',
    required: 'Required',
    example: 'Irvine',
    name: 'city',
  },
  {
    column_name: 'Participant2 mailing State',
    possible_values: 'State for the participant’s address.',
    required: 'Required',
    example: 'CA',
    name: 'state',
  },
  {
    column_name: 'Participant2 mailing ZIP Code',
    possible_values: 'ZIP code for the participant’s address.',
    required: 'Required',
    example: '92603',
    name: 'zip_code',
  },
  {
    column_name: 'Participant2 mailing County',
    possible_values: 'County for the participant’s address.',
    required: 'Optional',
    example: 'Orange',
    name: 'county',
  },
  {
    column_name: 'Participant3 Category',
    possible_values: 'Either "Individual" or "Entity".',
    required: 'Yes, in cases where the order has multiple participants.',
    example: 'Entity',
  },
  {
    column_name: 'Participant3 Type',
    possible_values: 'Value from "seller" or "buyer" or "borrower".',
    required: 'Yes, in cases where the order has multiple participants.',
    example: 'buyer',
  },
  {
    column_name: 'Participant3 First Name',
    possible_values: 'First name.',
    required:
      'If participant category is "Individual" and in cases where the order has multiple participants, then Yes; otherwise No',
    example: 'James',
    name: 'first_name',
    category: 'individual',
  },
  {
    column_name: 'Participant3 Middle Name',
    possible_values: 'Middle name.',
    required: 'Optional',
    example: 'Alan',
    name: 'middle_name',
    category: 'individual',
  },
  {
    column_name: 'Participant3 Last Name',
    possible_values: 'Last name.',
    required:
      'If participant category is "Individual" and in cases where the order has multiple participants, then Yes; otherwise No',
    example: 'Smith',
    name: 'last_name',
    category: 'individual',
  },
  {
    column_name: 'Participant3 Social Security Number',
    possible_values:
      'Social security number (Only applicable if participant category is "Individual").',
    required: 'Optional',
    example: '222-22-2222',
    name: 'ssn_number',
    category: 'individual',
  },
  {
    column_name: 'Participant3 Email',
    possible_values: 'Valid email address.',
    required: 'Yes, in cases where the order has multiple participants.',
    example: 'james.smith@gmail.com',
    name: 'email',
  },
  {
    column_name: 'Participant3 Phone',
    possible_values: 'Valid phone number.',
    required: 'Optional',
    example: '(222) 222-2222',
    name: 'mobile_phone',
  },
  {
    column_name: 'Participant3 Legal Business Name',
    possible_values: 'Legal business name (Only applicable if participant category is "Entity").',
    required:
      'If participant category is "Entity" and in cases where the order has multiple participants, then Yes; otherwise No',
    example: 'Tech LLC',
    name: 'business_name',
    category: 'entity',
  },
  {
    column_name: 'Participant3 Entity Type',
    possible_values:
      'Value from "Corporation", "LLC", "Trust", "Partnership", or "Other" (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'LLC',
    name: 'entity_type',
    category: 'entity',
  },
  {
    column_name: 'Participant3 Entity State',
    possible_values:
      'State abbreviation for the entity (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'AL',
    name: 'entity_state',
    category: 'entity',
  },
  {
    column_name: 'Participant3 Entity EIN',
    possible_values:
      'Employer Identification Number (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: '22-2222222',
    name: 'entity_ein',
    category: 'entity',
  },
  {
    column_name: 'Participant3 routing number',
    possible_values: 'Routing number for your bank account.',
    required: 'Required',
    example: '122199983',
    name: 'routing_number',
  },
  {
    column_name: 'Participant3 bank account number',
    possible_values: 'Account number for your bank account.',
    required: 'Required',
    example: '923305129348',
    name: 'bank_account_number',
  },
  {
    column_name: 'Participant3 name on account',
    possible_values: 'Name on account for your bank account.',
    required: 'Required',
    example: 'John Doe',
    name: 'name_on_account',
  },
  {
    column_name: 'Participant3 mailing address same as property address',
    possible_values:
      'Value either "yes" or "no"(If this is selected as "yes" you can skip adding participant mailing address details).',
    required: 'Yes, in cases where the order has multiple participants.',
    example: 'no',
    name: 'is_address_same_as_order_property',
  },
  {
    column_name: 'Participant3 mailing Address line 1',
    possible_values: 'Street number and route for the participant’s address.',
    required: 'Yes, in cases where the order has multiple participants.',
    example: '123 Main Street',
    name: 'address_line_1',
  },
  {
    column_name: 'Participant3 mailing Address line 2',
    possible_values: 'Mention other details about the address.',
    required: 'Optional',
    example: 'Near Central Park',
    name: 'address_line_2',
  },
  {
    column_name: 'Participant3 mailing Unit Number',
    possible_values: 'Unit number for the participant’s address.',
    required: 'Optional',
    example: 'A',
    name: 'unit_no',
  },
  {
    column_name: 'Participant3 mailing City',
    possible_values: 'City for the participant’s address.',
    required: 'Yes, in cases where the order has multiple participants.',
    example: 'Irvine',
    name: 'city',
  },
  {
    column_name: 'Participant3 mailing State',
    possible_values: 'State for the participant’s address.',
    required: 'Yes, in cases where the order has multiple participants.',
    example: 'CA',
    name: 'state',
  },
  {
    column_name: 'Participant3 mailing ZIP Code',
    possible_values: 'ZIP code for the participant’s address.',
    required: 'Yes, in cases where the order has multiple participants.',
    example: '92603',
    name: 'zip_code',
  },
  {
    column_name: 'Participant3 mailing County',
    possible_values: 'County for the participant’s address.',
    required: 'Optional',
    example: 'Orange',
    name: 'county',
  },
];

export const guideTableDataPHH = () => [
  {
    column_name: 'Loan Skey',
    possible_values: 'Unique string for each order.',
    required: 'Required',
    example: 'VSS-21231231',
    name: 'escrow_number',
  },
  {
    column_name: 'Anticipated Close Date',
    possible_values: 'Date in the "MM/DD/YYYY" format.',
    required: 'Required',
    example: '09/31/2024',
    name: 'anticipated_close_date',
  },
  {
    column_name: 'Property Address Line 1',
    possible_values: 'Street number and route of the property address.',
    required: 'Required',
    example: '123 Main Street',
    name: 'property_address_1',
  },
  {
    column_name: 'Property Address Line 2',
    possible_values: 'Other details for your property address.',
    required: 'Required',
    example: 'Near Central Park',
    name: 'property_address_2',
  },
  {
    column_name: 'Property Unit Number',
    possible_values: 'Unit number for the property address, if applicable.',
    required: 'Optional',
    example: 'A',
    name: 'property_unit_no',
  },
  {
    column_name: 'Property City',
    possible_values: 'City for the property address.',
    required: 'Required',
    example: 'Irvine',
    name: 'property_city',
  },
  {
    column_name: 'Property State',
    possible_values: 'State for the property address.',
    required: 'Required',
    example: 'CA',
    name: 'property_state',
  },
  {
    column_name: 'Property ZIP Code',
    possible_values: 'ZIP code for the property address.',
    required: 'Required',
    example: '92603',
    name: 'property_zipcode',
  },
  {
    column_name: 'Property County',
    possible_values: 'County for the property address.',
    required: 'Required',
    example: 'Orange',
    name: 'property_county',
  },
  {
    column_name: 'Participant1 Category',
    possible_values: 'Either "individual" or "entity".',
    required: 'Required',
    example: 'Individual',
  },
  {
    column_name: 'Participant1 Type',
    possible_values: 'Value from "seller" or "buyer" or "borrower".',
    required: 'Required',
    example: 'seller',
  },
  {
    column_name: 'Participant1 routing number',
    possible_values: 'Routing number for your bank account.',
    required: 'Required',
    example: '122199983',
    name: 'routing_number',
  },
  {
    column_name: 'Participant1 bank account number',
    possible_values: 'Account number for your bank account.',
    required: 'Required',
    example: '923305129348',
    name: 'bank_account_number',
  },
  {
    column_name: 'Participant1 name on account',
    possible_values: 'Name on account for your bank account.',
    required: 'Required',
    example: 'John Doe',
    name: 'name_on_account',
  },
  {
    column_name: 'Participant1 First Name',
    possible_values: 'First name.',
    required: 'If participant category is "Individual", then Yes; otherwise No',
    example: 'John',
    name: 'first_name',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Last Name',
    possible_values: 'Last name.',
    required: 'If participant category is "Individual", then Yes; otherwise No',
    example: 'Doe',
    name: 'last_name',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Social Security Number',
    possible_values:
      'Social security number (Only applicable if participant category is "Individual").',
    required: 'Optional',
    example: '111-11-1111',
    name: 'ssn_number',
    category: 'individual',
  },
  {
    column_name: 'Participant1 Email',
    possible_values: 'Valid email address.',
    required: 'Required',
    example: 'john.doe@gmail.com',
    name: 'email',
  },
  {
    column_name: 'Participant1 Phone',
    possible_values: 'Valid phone number.',
    required: 'Optional',
    example: '(111) 111-2222',
    name: 'mobile_phone',
  },
  {
    column_name: 'Participant1 Legal Business Name',
    possible_values: 'Legal business name (Only applicable if participant category is "Entity").',
    required: 'If participant category is "Entity", then Yes; otherwise No',
    example: 'Tech Corporation',
    name: 'business_name',
    category: 'entity',
  },
  {
    column_name: 'Participant1 Entity Type',
    possible_values:
      'Value from "Corporation", "LLC", "Trust", "Partnership", or "Other" (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'Corporation',
    name: 'entity_type',
    category: 'entity',
  },
  {
    column_name: 'Participant1 Entity State',
    possible_values:
      'State abbreviation for the entity (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: 'CA',
    name: 'entity_state',
    category: 'entity',
  },
  {
    column_name: 'Participant1 Entity EIN',
    possible_values:
      'Employer Identification Number (Only applicable if participant category is "Entity").',
    required: 'Optional',
    example: '11-1111111',
    name: 'entity_ein',
    category: 'entity',
  },
  {
    column_name: 'Participant1 mailing address same as property address',
    possible_values:
      'Either "yes" or "no"(If this is selected as "Yes" you can skip adding participant mailing address details).',
    required: 'Required',
    example: 'yes',
    name: 'is_address_same_as_order_property',
  },
  {
    column_name: 'Participant1 mailing Address line 1',
    possible_values: 'Street number and route for the participant’s address.',
    required: 'Required',
    example: '1234 Main Street',
    name: 'address_line_1',
  },
  {
    column_name: 'Participant1 mailing Address line 2',
    possible_values: 'Mention other details about the address.',
    required: 'Optional',
    example: 'Near Central Park',
    name: 'address_line_2',
  },
  {
    column_name: 'Participant1 mailing Unit Number',
    possible_values: 'Unit number for the participant’s address.',
    required: 'Optional',
    example: 'A',
    name: 'unit_no',
  },
  {
    column_name: 'Participant1 mailing City',
    possible_values: 'City for the participant’s address.',
    required: 'Required',
    example: 'Irvine',
    name: 'city',
  },
  {
    column_name: 'Participant1 mailing State',
    possible_values: 'State for the participant’s address.',
    required: 'Required',
    example: 'CA',
    name: 'state',
  },
  {
    column_name: 'Participant1 mailing ZIP Code',
    possible_values: 'ZIP code for the participant’s address.',
    required: 'Required',
    example: '92603',
    name: 'zip_code',
  },
  {
    column_name: 'Participant1 mailing County',
    possible_values: 'County for the participant’s address.',
    required: 'Optional',
    example: 'Orange',
    name: 'county',
  },
];

export const participantAddressFields = [
  'address_line_1',
  'address_line_2',
  'unit_no',
  'city',
  'state',
  'county',
  'zip_code',
];
export const ID_MAPPING = { 2: 'Passport', 22: `Driver's License`, 1: 'ID Card' };
export const ID_MAPPING_VERIFF = { DRIVERS_LICENSE: 22, PASSPORT: 2, ID_CARD: 1 };

export const MAIN_BANK_QUE = '206f1d30-42b5-4ba3-911e-700583d9ff0f';
export const BANK_ACCOUNT_SECTION = '2e71ca86-24da-4683-b7ed-4aef34bb9ac3';
export const BANK_ACCOUNT_SECTION_2 = '18bf5f33-d3c0-4000-8325-9ebc34af9300';
export const BANK_ACCOUNT_SECTION_3 = 'ea880c86-7419-4b4c-8010-edaa38c7701b';
export const BANK_ACCOUNT_NUMBER = '47385310-da40-432e-b5a8-0e14d72127b7';
export const ROUTING_NUMBER_FIELD = 'f6d0f62e-86a9-4b35-81bf-c8c02257b103';
export const BANK_NAME = '33c3237a-099b-4a07-8a89-ed983a9924f9';
export const BANK_ACCOUNT_NAME = '92efb91c-9bdb-4577-8ed2-455134f0fd1c';
export const BANK_CITY = 'a1ca9a9c-e068-42b9-89f7-e83e810fce12';
export const BANK_STATE = '889dd184-3d56-4cce-970c-ca090f4d76b6';

export const IMM_FIELD = 'd853c8d3-0802-4fc7-8106-919439af8a4b';
export const IMM_BANK_ACCOUNT_NUMBER = 'df98fa5d-2dcb-4a72-8375-3bb2aba21bc6';
export const IMM_ROUTING_NUMBER_FIELD = 'c3ada41a-bab5-4676-97ef-5dc83c1972c7';

export const COR_FIELD = 'dda1fa32-4a36-4249-a759-ad255d22d8d1';
export const COR_BANK_ACCOUNT_NUMBER = '2bc6551f-f250-4b91-b223-112e1b6b8600';
export const COR_ROUTING_NUMBER_FIELD = '6cca8058-03aa-436b-acd8-de0fb7c22458';

export const ACCOUNT_NAME = 'Bank Account Number';
export const ROUTING_NAME = 'Bank ABA';
export const ROUTING_NAME_2 = 'Bank ABA (Routing Number)';
export const NAME_OF_ACCOUNT_NAME = 'Name on Account';
export const BANK_CITY_NAME = 'Bank City';
export const BANK_STATE_NAME = 'Bank State';
export const BANK_NAME_NAME = 'Bank Name';
export const BANK_ACCOUNT_SAME_AS_PRIMARY = '_bank_account_same_as_';
export const BANK_ACCOUNT_PARENT = 'core_proceeds';
export const BORROWER_BANK_ACCOUNT_PARENT = 'borrower_field_62168';
export const INTERMEDIARY_BANK_ACCOUNT_PARENT = 'intermediary_bank';
export const CORRESPONDENT_BANK_ACCOUNT_PARENT = 'correspondent_bank';

export const EMPLOYMENT_SECTION = '192004cc-f710-4000-882a-2eced76a2100';
export const LIABILITIES_SECTION = '19231354-0780-4000-8f0f-b8741b02df00';
export const ASSETS_SECTION = '19231352-d7e0-4000-8d7b-05cd022d8a00';

export const FIELDS_TO_DISABLE = [BANK_CITY_NAME, BANK_STATE_NAME, BANK_NAME_NAME];

export const ADDITIONAL_PART_GROUP = '711ccb04-0bd8-4f16-aace-cfb43086897f';

export const SKIP_SECTION_IDS = [
  'c22663ed-489b-454a-9d63-5b77579b5134',
  '2e71ca86-24da-4683-b7ed-4aef34bb9ac3',
  '18bf5b7b-31d0-4000-89a3-14354c485700',
  'ea880c86-7419-4b4c-8010-edaa38c7701b',
  '192004cc-f710-4000-882a-2eced76a2100',
  '19231352-d7e0-4000-8d7b-05cd022d8a00',
  '19231354-0780-4000-8f0f-b8741b02df00',
  '18bf5f33-d3c0-4000-8325-9ebc34af9300',
  '18bf5b7b-31d0-4000-89a3-14354c485700',
  '19eb018e-2390-4000-86ee-a3618336cc80',
];

export const DL_AVAILABLE_TYPE = ['Seller', 'Borrower', 'Buyer'];

export const EXCLUDED_KEYS = [
  'page',
  'limit',
  'client_id',
  'client_office_id',
  'from_client',
  'elementId',
  'from_order',
  'highlighted_order',
];
export const MIN_NAME = 'core_lender_min';

export const BAV_CODE_DESC = {
  BVCI: 'The bank account is either closed, invalid, or in a negative status. We verified this information using the routing number and account number.',
  BVDRL:
    'The bank account is either ineligible for a debit activity or has a high probability of return. We verified this information using the routing number and account number.',
  BVONI:
    'We could not verify the routing number and account number for this bank account. We verified this information using the routing number and account number provided by our bank data provider.',
  BAOM: "The bank account owner's information matches the information provided. We verified this information using the first name and last name or the business name.",
  BAONM:
    "The bank account owner's information does not match with the information provided. We verified this information using the first name and last name or the business name.",
  BANI: 'Our bank data provider could not authenticate this bank account since they could not find any information about this bank account.',
  BLIAR:
    'Probably an invalid account number or routing number. We verified this information using the routing number and account number.',
  BOBAONM:
    'Bank account owner information does not match with the information provided. We verified this information using the first name and last name provided by an open banking data provider.',
  BVSUC:
    'The bank data provider performed the verification of this account. This doesn’t mean the verification was successful, it just means we received some data from the data provider.',
  BVER: 'Bank data provider error. The bank data provider returned an error while trying to retrieve the bank account information.',
  BVEDPT:
    'Bank data provider timeout. The bank data provider timed out while trying to retrieve the bank account information.',
  BVEDIO:
    'The bank data provider returned an error indicating that invalid owner information was submitted. This typically means data quality errors, where the input cannot be processed, for example, strange letters or symbols. This can be due to incorrect first name or last name for personal bank accounts or incorrect business name for business bank accounts.',
  BVEIBS:
    'The bank data provider returned an error indicating that the account number and routing number had invalid lengths. This can be due to incorrect account number or routing number or user error while entering numbers.',
  BAOMP:
    "Bank account owner's information is partially matched. We verified this information by using first name and last name or the business name.",
  BANEW: 'The bank account is open and valid. But, it was opened very recently (less than 7 days).',
  BALOWT:
    'The bank account is open and valid. But, it was opened recently (between 7 and 30 days).',
  BAMEDT:
    'The bank account is open and valid. It has a medium tenure (between 30 days and 1 year).',
  BALNGT:
    'The bank account is open and valid. The bank account has a long tenure (between 1 year and 3 years).',
  BAVLNGT:
    'The bank account is open and valid. The bank account has a very long tenure (greater than 3 years).',
  BPHM: "The bank account owner's phone number matches the information provided by the bank data provider.",
  BPHNOM:
    "The bank account owner's phone number doesn’t match the information provided by the bank data provider.",
  BDOBM:
    "The bank account owner's date of birth (DOB) matches the information provided by the bank data provider.",
  BDOBNOM:
    "The bank account owner's date of birth (DOB) doesn’t match the information provided by the bank data provider.",
  BTAXM:
    "The bank account owner's SSN or Tax ID matches the information provided by the bank data provider.",
  BTAXNOM:
    "The bank account owner's SSN or Tax ID doesn’t match the information provided by the bank data provider.",
  BADDRM:
    "The bank account owner's address matches the information provided by the bank data provider.",
  BADDRNOM:
    "The bank account owner's address doesn’t match the information provided by the bank data provider.",
  BEMAILM:
    "The bank account owner's email address matches the information provided by the bank data provider.",
  BEMAILNOM:
    "The bank account owner's email address doesn’t match the information provided by the bank data provider.",
  BACVST: 'The bank account is closed and invalid. It was closed less than 7 days ago.',
  BACST: 'The bank account is closed and invalid. It was closed between 7 and 30 days ago.',
  BACMT: 'The bank account is closed and invalid. It was closed between 30 days and 1 year ago.',
  BACLT: 'The bank account is closed and invalid. It was closed more than 1 year and 3 years ago.',
  BACVLT: 'The bank account is closed and invalid. It was closed more than 3 years ago.',
  BNIF: 'The bank data provider did not find any information about the bank. This could be because our provider doesn’t have data for this bank.',
  BENI: 'We were unable to retrieve bank information for the routing number, even though we have a high success rate of retrieving information for that specific bank from our bank data provider.',
  BVOV: 'The bank account is open and valid. We verified this information using the routing number and account number.',
  BVSCU:
    'The bank account is closed as a result of unacceptable (bad-behavior) use by the customer. This data was provided by an external third-party bank data provider.',
  BVSCR:
    'The bank account is removed from the contributor’s account file and it will be retained by the bank data provider until it is reissued by the contributor. This data was provided by an external third-party bank data provider. Note: Here, the contributor is the financial institution that is sending data to our bank data provider.',
  BVSDA:
    'The bank account is not set up for any debit activity. You cannot transfer or remove money from this account. This data was provided by an external third-party bank data provider.',
  BVSCC:
    'The bank account is closed and removed from the Contributor’s source data. This data was provided by an external third-party bank data provider.',
  BVSCP:
    'The bank account is closed in the last processing cycle. This data was provided by an external third-party bank data provider.',
  BVSBD:
    'The bank account has a history of available balance being negative (or in overdraft). This data was provided by an external third-party bank data provider.',
  BVSIAC:
    'The bank account is known to be the correspondent deposit account (non-DDA account). This data was provided by an external third-party bank data provider.',
  BVSHR:
    'The bank account has a high probability of getting an ACH return. This data was provided by an external third-party bank data provider.',
};

export const OFAC_LIST_SEARCH_LINK = 'https://sanctionssearch.ofac.treas.gov/';

// Reopen sections mapping
export const REOPEN_SECTIONS = {
  buyer: [
    {
      id: BUYER_SECTION,
      label: 'Buyer Information section',
    },
  ],
  borrower: [
    {
      id: SOI_SECTION_ID,
      label: 'Statement Of Identity',
    },
    {
      id: BANK_ACCOUNT_SECTION_2,
      label: 'Closing & Disbursement(Bank Account Section)',
    },
  ],
  seller: [
    {
      id: SOI_SECTION_ID,
      label: 'Statement Of Identity',
    },
    {
      id: BANK_ACCOUNT_SECTION,
      label: 'Closing & Disbursement(Bank Account Section)',
    },
  ],
};

// Buyer Spouse related fields

export const BUYER_MARITAL_STATUS_NAME = 'buyer_marital_status';
export const BUYER_SPOUSE_NAME_NAME = 'buyer_spouse_name';
export const IS_SECOND_BUYER_NAME = 'core_second_buyer';
export const ADDITIONAL_BUYER_INDIVIDUAL_ENTITY_NAME = 'buyer_type';
export const ADDITIONAL_BUYER_INDIVIDUAL_ENTITY_ID = '194f3f72-6350-4000-814f-d71dd8149d00';
export const ADDITIONAL_BUYER_FIRST_NAME = '18bf5e01-2e10-4000-8af7-27d59fc00c00';
export const ADDITIONAL_BUYER_LAST_NAME = '19bc02f8-ddd0-4000-81d3-e29fc14a7200';
export const IS_SECOND_BUYER_NAME_ID = '18bf5d33-8ba0-4000-8349-86726cf91500';

export const SURVEY_STEPS = [
  {
    section_name: 'Overall Experience',
    fields: [
      {
        id: 'overall_experience',
        label: 'How would you rate your overall experience with ClientVerify.ai? ',
        type: 'rating',
        max: 5,
        start_label: 'Terrible',
        end_label: 'Amazing',
      },

      {
        id: 'time_expectation',
        label:
          'Were you able to complete the questionnaire without assistance from a human representative?',
        type: 'radio',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
    ],
  },
  {
    section_name: 'Platform Usability',
    fields: [
      {
        id: 'form_clarity',
        label: 'How easy is the platform to use?',
        type: 'rating',
        max: 5,
        start_label: 'Not easy at all',
        end_label: 'Very easy',
      },

      {
        id: 'form_clarity_help',
        label: 'While using ClientVerify, how clear was it to know what to do next?',
        type: 'rating',
        max: 5,
        start_label: 'I had no idea',
        end_label: 'Very clear',
      },
      {
        id: 'form_understanding',
        label: 'At what point did you understand what ClientVerify was doing?',
        type: 'radio',
        options: [
          { label: 'Immediately', value: 'immediately' },
          { label: 'After a few steps', value: 'after_a_few_steps' },
          { label: 'Near the end', value: 'near_the_end' },
          { label: 'I never fully understood', value: 'never' },
        ],
      },
      {
        id: 'tool_understanding',
        label: 'In your own words, what do you think the tool is doing?',
        type: 'textarea',
      },
      {
        id: 'email_notification',
        label: 'How clear are our emails and notifications?',
        type: 'rating',
        max: 5,
        start_label: 'I had no idea what was going on',
        end_label: 'I felt very comfortable knowing my next steps',
      },
      {
        id: 'slow_down',
        label: 'Were there any moments where you paused or slowed down?',
        type: 'radio',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        id: 'slow_down_reason',
        label: 'If you paused or slowed down, why did you do so?',
        type: 'textarea',
        placeholder: 'Optional',
      },
      {
        id: 'skip_section',
        label: 'What sections (if any) did you consider skipping or abandoning?',
        type: 'textarea',
        placeholder: 'Optional',
      },
      {
        id: 'security_comfort',
        label: 'How comfortable did you feel with our security measures?',
        type: 'rating',
        max: 5,
        start_label: 'Not at all',
        end_label: 'Very comfortable',
      },
    ],
  },
  {
    section_name: 'Time & Effort',
    fields: [
      {
        id: 'time_expectation',
        label: 'Did the form take more time than you expected?',
        type: 'radio',
        options: [
          { label: 'Much less', value: 'much_less' },
          { label: 'About the same', value: 'same' },
          { label: 'A little more', value: 'slightly_more' },
          { label: 'Much more', value: 'much_more' },
        ],
      },
    ],
  },
  {
    section_name: 'Additional Feedback',
    fields: [
      {
        id: 'confusing_part',
        label: 'What was the most confusing or frustrating part, if any?',
        type: 'textarea',
      },
      {
        id: 'expected_happened',
        label: `Was there anything you expected to happen that didn't? Or vice versa?`,
        type: 'textarea',
      },
      {
        id: 'simplify_one_thing',
        label: 'If you could remove or simplify one thing, what would it be?',
        type: 'textarea',
      },
      {
        id: 'improvement_suggestion',
        label: 'What can we improve to make this process better?',
        type: 'textarea',
      },
    ],
  },
  {
    section_name: 'Final Question',
    fields: [
      {
        id: 'recommendation',
        label: 'Would you recommend this process to someone in a similar situation?',
        type: 'radio',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'Maybe', value: 'maybe' },
          { label: 'No', value: 'no' },
        ],
      },
    ],
  },
  {
    section_name: 'Thank You!',
    fields: [],
    section_type: 'thank_you',
  },
];

export const bulkUserUploadGuideTableData = () => [
  {
    column_name: 'client offices',
    possible_values: 'Office Names (comma separated if multiple).',
    required: 'Required for Office Admin and Office User roles.',
    example: 'Office A, Office B',
    name: 'client_office_id',
  },
  {
    column_name: 'user role',
    possible_values:
      'Role of the user. Values: "client admin", "client user", "office admin", "office user", "realtor" (only if Realtor access is enabled for the client).',
    required: 'Required',
    example: 'office user',
    name: 'role_type',
  },
  {
    column_name: 'role in company',
    possible_values:
      'Title shown to participants on their form, e.g. "Closer" or "Processor". Defaults to "Closer" if blank.',
    required: 'Optional',
    example: 'Processor',
    name: 'user_role_in_company',
  },
  {
    column_name: 'first name',
    possible_values: 'First name of the user.',
    required: 'Required',
    example: 'John',
    name: 'first_name',
  },
  {
    column_name: 'last name',
    possible_values: 'Last name of the user.',
    required: 'Required',
    example: 'Doe',
    name: 'last_name',
  },
  {
    column_name: 'email',
    possible_values: 'Valid email address.',
    required: 'Required',
    example: 'john.doe@example.com',
    name: 'email',
  },
  {
    column_name: 'phone_no',
    possible_values: 'Phone number of the user, 7–20 characters (digits, spaces, + - ( ) .).',
    required: 'Optional',
    example: '(555) 123-4567',
    name: 'phone_no',
  },
  {
    column_name: 'password',
    possible_values: 'Password for the user (min 6 characters).',
    required: 'Required',
    example: 'Password123!',
    name: 'password',
  },
  {
    column_name: 'user timezone',
    possible_values: 'Valid timezone.',
    required: 'Required',
    example: 'PST/EST',
    name: 'timezone',
  },
  {
    column_name: 'status',
    possible_values: '"active" or "inactive".',
    required: 'Required',
    example: 'active',
    name: 'status',
  },
];

export const bulkUserUploadSampleDataSetCSV = (officeListOptions) => [
  [
    {
      column: 'client offices',
      value: officeListOptions?.[0]?.office_name || '',
      name: 'client_office_id',
    },
    {
      column: 'user role',
      value: 'office user',
      name: 'role_type',
    },
    {
      column: 'role in company',
      value: 'Closer',
      name: 'user_role_in_company',
    },
    {
      column: 'first name',
      value: 'John',
      name: 'first_name',
    },
    {
      column: 'last name',
      value: 'Doe',
      name: 'last_name',
    },
    {
      column: 'email',
      value: 'john.doe@example.com',
      name: 'email',
    },
    {
      column: 'phone_no',
      value: '(555) 123-4567',
      name: 'phone_no',
    },
    {
      column: 'password',
      value: 'Password123!',
      name: 'password',
    },
    {
      column: 'user timezone',
      value: 'PST',
      name: 'timezone',
    },
    {
      column: 'status',
      value: 'active',
      name: 'status',
    },
  ],
  [
    {
      column: 'client offices',
      value: '',
      name: 'client_office_id',
    },
    {
      column: 'user role',
      value: 'client admin',
      name: 'role_type',
    },
    {
      column: 'role in company',
      value: 'Processor',
      name: 'user_role_in_company',
    },
    {
      column: 'first name',
      value: 'Jane',
      name: 'first_name',
    },
    {
      column: 'last name',
      value: 'Smith',
      name: 'last_name',
    },
    {
      column: 'email',
      value: 'jane.smith@example.com',
      name: 'email',
    },
    {
      column: 'phone_no',
      value: '',
      name: 'phone_no',
    },
    {
      column: 'password',
      value: 'SecurePass456!',
      name: 'password',
    },
    {
      column: 'user timezone',
      value: 'EST',
      name: 'timezone',
    },
    {
      column: 'status',
      value: 'active',
      name: 'status',
    },
  ],
  [
    {
      column: 'client offices',
      value: officeListOptions?.map((ofc) => ofc.office_name).join(',') || '',
      name: 'client_office_id',
    },
    {
      column: 'user role',
      value: 'office admin',
      name: 'role_type',
    },
    {
      column: 'role in company',
      value: '',
      name: 'user_role_in_company',
    },
    {
      column: 'first name',
      value: 'Robert',
      name: 'first_name',
    },
    {
      column: 'last name',
      value: 'Brown',
      name: 'last_name',
    },
    {
      column: 'email',
      value: 'robert.brown@example.com',
      name: 'email',
    },
    {
      column: 'phone_no',
      value: '(555) 987-6543',
      name: 'phone_no',
    },
    {
      column: 'password',
      value: 'MySecret789$',
      name: 'password',
    },
    {
      column: 'user timezone',
      value: 'CST',
      name: 'timezone',
    },
    {
      column: 'status',
      value: 'inactive',
      name: 'status',
    },
  ],
];

export const PROPERTY_TYPE_OPTIONS = [
  {
    label: 'Single Family Residence',
    value: 'SFR',
  },
  {
    label: 'Two Units',
    value: 'TWO UNITS',
  },
  {
    label: 'Three Units',
    value: 'THREE UNITS',
  },
  {
    label: 'Four Units',
    value: 'FOUR UNITS',
  },
  {
    label: 'PUD',
    value: 'PUD',
  },
  {
    label: 'Condo',
    value: 'CONDOMINIUM',
  },
  {
    label: 'CoOp',
    value: 'COOPERATIVE',
  },
  {
    label: 'Raw/Unimproved',
    value: 'RAW/UNIMPROVED',
  },
  {
    label: 'Vacant Land',
    value: 'VACANT LAND (NEC)',
  },
  {
    label: 'Mobile Home',
    value: 'MOBILE HOME',
  },
  {
    label: 'Modular',
    value: 'MODULAR',
  },
  {
    label: 'Other',
    value: 'OTHER',
  },
];

export const NAME_MATCHING_MODE_LABELS = {
  strict_name_validation: 'Strict Name Matching',
  match_middle_name_initial: 'Match Middle Name Initial',
  ignore_middle_name: 'Ignore Middle Name',
};

export const FRAUD_TYPE_OPTIONS = [
  { value: 'seller_fraud', label: 'Seller Fraud (non-owner)' },
  { value: 'family_fraud', label: 'Family Fraud (relative of true owner)' },
  { value: 'fake_id', label: 'Fake ID' },
  { value: 'romance_scheme', label: 'Romance scheme' },
  { value: 'other', label: 'Other' },
];

export const FRAUD_TYPE_LABELS = FRAUD_TYPE_OPTIONS?.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

export const ADDRESS_GROUP_ID_SELLER = 'c8b9c2f2-d476-4e23-a610-5d32346af5db';

export const ENTITY_TRUSTEE_MEMBERS_LABEL = '_entity_member_label';

export const REMOVE_TRUSTEE_MEMBER_LABEL = 'remove_trustee_member_label';

export const ID_ONLY_TX_NAME = 'id only';

export const WIRING_INSTRUCTION_DOC_TYPE = 'wire_instruction';